import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Typography,
  IconButton,
  Tooltip,
  Box,
  Button,
  TextField,
  InputAdornment,
  Grid,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Visibility,
  Group,
  CheckCircleOutline,
  CancelOutlined,
  EventBusy,
  EditOutlined,
} from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import { useDispatch, useSelector } from "react-redux";
import ReusableGrid from "../../components/ag grid/ReusableGrid";
import AttendanceViewModal from "./AttendanceView";
import MarkAttendanceModal from "./MarkAttendanceModal";
import AddReportMonth from "./AddAttendanceReportMonth";
import PageSizeSelector from "../../components/ag grid/PageSizeSelector";
import ListPageLayout from "../../components/layout/ListPageLayout";
import "../../styles/styles.css";
import "../../styles/layout.css";
import "../../styles/attendance.css";
import usePermission from "../../utils/usePermission";
import AttendanceVeiwController from "../../redux/actions/AttendenceApiAction";
import { getUserInfoFromToken } from "../../utils/authUtils";
import { toolbarButtonSx } from "../../components/auth/authTheme";
import AttendanceMobileView from "./AttendanceMobileView";

const STATUS_CLASS = {
  P: "present",
  A: "absent",
  O: "offday",
  R: "restday",
  L: "leave",
  H: "holiday",
  E: "early",
};

const AttendanceTable = () => {
  const dispatch = useDispatch();
  const gridRef = useRef(null);
  const buttonRef = useRef(null);
  const mobileReportButtonRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [userType, setUserType] = useState(null);
  const { getEmployee } = AttendanceVeiwController();
  const { employee } = useSelector((state) => state.TimeAttendance);

  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [markEmployees, setMarkEmployees] = useState([]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showMarkModal, setShowMarkModal] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const { hasPermission } = usePermission();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterDate, setFilterDate] = useState(new Date().toISOString().split("T")[0]);

  useEffect(() => {
    dispatch(getEmployee(filterDate));
  }, [dispatch, filterDate]);

  useEffect(() => {
    const { userType: type } = getUserInfoFromToken();
    setUserType(type);
  }, []);

  const handleView = useCallback((row) => {
    setSelectedEmployee(row);
    setShowViewModal(true);
  }, []);

  const handleMark = useCallback((row) => {
    setMarkEmployees([row]);
    setShowMarkModal(true);
  }, []);

  const handleBulkMark = () => {
    setMarkEmployees(selectedRows);
    setShowMarkModal(true);
  };

  const handleMarkSuccess = () => {
    dispatch(getEmployee(filterDate));
    setSelectedRows([]);
  };

  const handleClearSelection = () => {
    gridRef.current?.deselectAll();
    setSelectedRows([]);
    setOpenDialog(false);
  };

  const handleToggleSelect = useCallback((employee) => {
    setSelectedRows((prev) => {
      const exists = prev.some((row) => row.id === employee.id);
      if (exists) {
        return prev.filter((row) => row.id !== employee.id);
      }
      return [...prev, employee];
    });
  }, []);

  const finalEmployees = useMemo(() => {
    const list = employee || [];
    if (!searchTerm.trim()) return list;

    const q = searchTerm.toLowerCase();
    return list.filter(
      (emp) =>
        emp.first_name?.toLowerCase().includes(q) ||
        emp.last_name?.toLowerCase().includes(q) ||
        emp.email?.toLowerCase().includes(q) ||
        emp.mobile_number?.toLowerCase().includes(q) ||
        emp.employee_id?.toLowerCase().includes(q)
    );
  }, [employee, searchTerm]);

  const stats = useMemo(() => {
    const list = employee || [];
    return {
      total: list.length,
      present: list.filter((e) => e.status === "P").length,
      absent: list.filter((e) => e.status === "A").length,
      onLeave: list.filter((e) => e.status === "L").length,
    };
  }, [employee]);

  const formatClockTime = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  };

  const columnDefs = useMemo(
    () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        width: 52,
        maxWidth: 52,
        minWidth: 52,
        pinned: "left",
        suppressSizeToFit: true,
        sortable: false,
        headerClass: "checkbox-selection-header",
        cellClass: "checkbox-selection-cell",
      },
      {
        headerName: "SR.No",
        valueGetter: (params) => params.node.rowIndex + 1,
        width: 72,
        maxWidth: 80,
        minWidth: 72,
        suppressSizeToFit: true,
      },
      {
        headerName: "Emp ID",
        field: "employee_id",
        width: 100,
        minWidth: 90,
        maxWidth: 120,
        suppressSizeToFit: true,
      },
      {
        headerName: "Name",
        valueGetter: (params) =>
          `${params.data.first_name || ""} ${params.data.last_name || ""}`.trim(),
        flex: 1.5,
        minWidth: 160,
      },
      {
        headerName: "Clock In (today)",
        field: "clock_in",
        width: 130,
        minWidth: 120,
        maxWidth: 150,
        suppressSizeToFit: true,
        valueFormatter: (params) => formatClockTime(params.value),
      },
      {
        headerName: "Clock Out (today)",
        field: "clock_out",
        width: 130,
        minWidth: 120,
        maxWidth: 150,
        suppressSizeToFit: true,
        valueFormatter: (params) => formatClockTime(params.value),
      },
      {
        headerName: "Status",
        field: "status",
        width: 100,
        minWidth: 90,
        maxWidth: 110,
        suppressSizeToFit: true,
        cellRenderer: (params) => {
          const code = params.value || "-";
          const className = STATUS_CLASS[code] || "unknown";
          return (
            <span className={`attendance-status-badge ${className}`}>
              {code}
            </span>
          );
        },
      },
      {
        headerName: "Actions",
        field: "actions",
        pinned: "right",
        width: 90,
        minWidth: 90,
        maxWidth: 90,
        suppressSizeToFit: true,
        sortable: false,
        cellRenderer: (params) => (
          <div style={{ display: "flex", justifyContent: "center", gap: "8px" }}>
            <Tooltip title="Mark Attendance">
              <IconButton
                color="secondary"
                size="small"
                onClick={() => handleMark(params.data)}
              >
                <EditOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="View Attendance">
              <IconButton
                color="primary"
                size="small"
                onClick={() => handleView(params.data)}
              >
                <Visibility fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        ),
      },
    ],
    [handleView, handleMark]
  );

  const canGenerateReport =
    userType === "Admin" ||
    hasPermission("attendance_management", "generate_report");
  const reportAnchorRef = isMobile ? mobileReportButtonRef : buttonRef;

  return (
    <>
      {isMobile ? (
        <AttendanceMobileView
          employees={finalEmployees}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filterDate={filterDate}
          setFilterDate={setFilterDate}
          handleView={handleView}
          handleMark={handleMark}
          canGenerateReport={canGenerateReport}
          selectedRows={selectedRows}
          onToggleSelect={handleToggleSelect}
          onOpenGenerateReport={() => setShowGenerateModal(true)}
          reportButtonRef={mobileReportButtonRef}
        />
      ) : (
        <ListPageLayout
          title="Time & Attendance"
          subtitle="Track daily attendance, clock times, and monthly records"
          tabs={
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <TextField
                type="date"
                size="small"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                inputProps={{ max: new Date().toISOString().split("T")[0] }}
                sx={{ backgroundColor: "white", borderRadius: 1 }}
              />
              {selectedRows.length > 0 && (
                <Button
                  variant="contained"
                  onClick={handleBulkMark}
                  sx={{
                    bgcolor: "#ff9800",
                    color: "white",
                    "&:hover": { bgcolor: "#f57c00" },
                  }}
                >
                  Mark Attendance
                </Button>
              )}
            </Box>
          }
          headerAction={
            canGenerateReport ? (
              <Button
                ref={buttonRef}
                variant="contained"
                color="primary"
                disabled={selectedRows.length === 0}
                onClick={() => setShowGenerateModal(true)}
                sx={toolbarButtonSx}
              >
                Generate Attendance Report
              </Button>
            ) : null
          }
          toolbarRight={
            <>
              <TextField
                size="small"
                placeholder="Search employees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ minWidth: 240 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <PageSizeSelector
                pageSize={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              />
            </>
          }
          stats={
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6} md={3}>
                <div className="stat-card stat-card-compact">
                  <div className="avatar rounded-circle bg-primary">
                    <Group className="white-icon" />
                  </div>
                  <div className="stat-card-compact-body">
                    <h6>Total Employees</h6>
                    <h3 className="value">{stats.total}</h3>
                  </div>
                </div>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <div className="stat-card stat-card-compact">
                  <div className="avatar rounded-circle bg-info">
                    <CheckCircleOutline className="white-icon" />
                  </div>
                  <div className="stat-card-compact-body">
                    <h6>Present Today</h6>
                    <h3 className="value">{stats.present}</h3>
                  </div>
                </div>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <div className="stat-card stat-card-compact">
                  <div className="avatar rounded-circle bg-primary">
                    <CancelOutlined className="white-icon" />
                  </div>
                  <div className="stat-card-compact-body">
                    <h6>Absent Today</h6>
                    <h3 className="value">{stats.absent}</h3>
                  </div>
                </div>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <div className="stat-card stat-card-compact">
                  <div className="avatar rounded-circle bg-danger">
                    <EventBusy className="white-icon" />
                  </div>
                  <div className="stat-card-compact-body">
                    <h6>On Leave</h6>
                    <h3 className="value">{stats.onLeave}</h3>
                  </div>
                </div>
              </Grid>
            </Grid>
          }
        >
          <div className="list-page-grid">
            <ReusableGrid
              rowData={finalEmployees}
              columnDefs={columnDefs}
              gridRef={gridRef}
              pagination={true}
              pageSize={pageSize}
              onSelectionChanged={(rows) => setSelectedRows(rows)}
              emptyMessage="No employees found"
              emptyHint="Adjust your search or add employees from the employee list."
            />
          </div>
        </ListPageLayout>
      )}

      <AttendanceViewModal
        show={showViewModal}
        handleClose={() => setShowViewModal(false)}
        selectedEmployee={selectedEmployee}
      />

      <MarkAttendanceModal
        show={showMarkModal}
        handleClose={() => setShowMarkModal(false)}
        employees={markEmployees}
        onSuccess={handleMarkSuccess}
      />

      {showGenerateModal && (
        <AddReportMonth
          open={showGenerateModal}
          handleClose={() => setShowGenerateModal(false)}
          selectedIds={selectedRows.map((row) => row.id)}
          anchorRef={reportAnchorRef}
          onSuccess={() => setOpenDialog(true)}
        />
      )}

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { mx: 2, borderRadius: "12px" } }}
      >
        <DialogTitle>
          <Typography variant="h6" component="div" sx={{ fontWeight: "bold" }}>
            Attendance report generated
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Report generated for <strong>{selectedRows.length}</strong> employee
            {selectedRows.length > 1 ? "s" : ""}. Clear the current selection?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, flexDirection: { xs: "column", sm: "row" }, gap: 1 }}>
          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            width="100%"
            gap={1}
          >
            <Button
              onClick={() => setOpenDialog(false)}
              variant="outlined"
              color="inherit"
              fullWidth
              sx={{ borderRadius: 2 }}
            >
              Keep Selection
            </Button>
            <Button
              onClick={handleClearSelection}
              variant="contained"
              color="primary"
              fullWidth
              sx={{ ...toolbarButtonSx, borderRadius: 2 }}
            >
              Clear Selection
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AttendanceTable;
